import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${BACKEND_URL}/api`, withCredentials: true });

// Attach current user email to every request as a fallback for auth when cookies are unavailable
api.interceptors.request.use((config) => {
  try {
    const saved = localStorage.getItem("usair_user");
    if (saved) {
      const u = JSON.parse(saved);
      if (u?.email) config.headers["X-User-Email"] = u.email;
    }
  } catch { /* ignore */ }
  // iter124 — Conversation system bearer session: when an external user has
  // redeemed a magic-link OTP, their session token is stored under
  // `usair_conv_session`. Send it as Authorization: Bearer cs_<…> on every
  // /conv/* request so the backend's _resolve_actor() can recognise them.
  //
  // When a full portal user (Google OAuth / localStorage usair_user) is
  // also present, we skip the conv-session header — the portal session
  // is authoritative and stale conv sessions would otherwise shadow it
  // (backend now falls through anyway, but not sending the header at all
  // is cleaner and marginally faster).
  try {
    const sess = localStorage.getItem("usair_conv_session");
    const saved = localStorage.getItem("usair_user");
    const url = String(config.url || "");
    if (sess && !saved && url.startsWith("/conv")) {
      config.headers["Authorization"] = `Bearer ${sess}`;
    }
  } catch { /* ignore */ }
  // Forward the active UI language so backend AI prompts (warranty summary,
  // assistant chat, etc.) reply in the user's language. Read from the same
  // localStorage key used by lib/i18n.jsx — no hard import to avoid cycles.
  try {
    const lang = localStorage.getItem("usair_lang");
    if (lang) config.headers["X-User-Lang"] = lang;
  } catch { /* ignore */ }
  return config;
});

/**
 * Global error interceptor — surfaces silent backend crashes to the user
 * via a sonner toast with a "Copy details" action.
 *
 * Strategy:
 *   • 5xx ("Internal server error") — ALWAYS toast. These are backend bugs;
 *     the user should know rather than seeing "nothing happened".
 *   • 4xx — skip. Calling code typically renders inline error states.
 *   • Network / timeout — toast (treat as 0).
 *
 * Each 5xx response from the backend now carries an `incident_id` (set by
 * server.py's _generic_exc_handler_logged via error_log_routes.write_error_row).
 * We surface that ID first because it's the fastest path to root cause:
 * admin can paste it into /admin/error-log/{id} and read the full stack trace.
 */
api.interceptors.response.use(
  (resp) => resp,
  (error) => {
    try {
      const status = error?.response?.status ?? 0;
      const isNetwork = !error?.response;
      const cfg = error?.config || {};
      // iter136.16 — opt-out for background polls. Endpoints that fire
      // silently on a timer (badge counts, presence pings) shouldn't
      // surface a noisy toast on every transient network blip — they
      // retry on their own cadence. Calls pass ``{ silent: true }`` in
      // the axios config to opt in.
      const silent = !!cfg.silent;
      // iter150AZ.50 — Suppress the noisy 5xx toast when the
      // backend itself signals a transient DB-availability blip
      // (``code: "db_unavailable"``). Reps already see "Database
      // unreachable" in the connection-status widget; spamming
      // them with a toast on every retry just adds noise.
      //
      // iter186-v3 — also suppress the Cloudflare-origin family of
      // gateway errors (502 Bad Gateway, 504 Gateway Timeout, 520
      // Origin Unknown Error, 521 Web Server Down, 522 Connection
      // Timed Out, 523 Origin Unreachable, 524 Timeout). These ALL
      // mean "Cloudflare couldn't get a clean response from our
      // origin" — almost always because MariaDB is unreachable and
      // a sync handler is still trying to connect. The DB-status
      // banner already communicates this; the toast is redundant
      // noise. (Real backend 500s — bugs — still toast.)
      const isDbBlip =
        error?.response?.status === 503
        && error?.response?.data?.code === "db_unavailable";
      const isCfGatewayBlip = [502, 504, 520, 521, 522, 523, 524].includes(status);
      const shouldToast = !silent && !isDbBlip && !isCfGatewayBlip && (isNetwork || status >= 500);
      if (!shouldToast) {
        return Promise.reject(error);
      }
      const incidentId = error?.response?.data?.incident_id || "";
      const detail = error?.response?.data?.detail || error?.message || "Network error";
      const path = (cfg.url || "").replace(/^\/+/, "/");
      const method = (cfg.method || "").toUpperCase();
      const headline = isNetwork
        ? "Network error — couldn't reach the server"
        : (incidentId
            ? `Error ${incidentId} — server hit a snag`
            : "Server error");
      const subline = `${method} ${path}: ${String(detail).slice(0, 160)}`;
      const detailBlob = JSON.stringify({
        when: new Date().toISOString(),
        method, path,
        status,
        incident_id: incidentId,
        detail,
        url: cfg.baseURL ? `${cfg.baseURL}${path}` : path,
      }, null, 2);
      toast.error(headline, {
        description: subline,
        duration: 10000,
        action: {
          label: "Copy details",
          onClick: () => {
            try {
              navigator.clipboard?.writeText(detailBlob);
              toast.success("Error details copied — paste in chat or Slack.");
            } catch { /* ignore */ }
          },
        },
      });
    } catch { /* never blow up the original promise */ }
    return Promise.reject(error);
  },
);

// iter186-v11 — by default this returns cheap circuit state (sub-ms,
// no MariaDB TCP traffic). Pass {probe: true} from the "Retry
// connection" button to force a live TCP probe.
export const checkDbStatus = (opts = {}) =>
  api.get("/db-status", {
    silent: true,
    params: opts.probe ? { probe: "true" } : undefined,
  }).then(r => r.data);

// iter150AZ.94 — Egress IP (admin-gated). The server's outbound IPv4
// so an admin can whitelist it on the MariaDB host / upstream APIs.
export const fetchEgressIp = (requester_email, { refresh = false } = {}) =>
  api.get("/admin/egress-ip", { params: { requester_email, refresh } }).then(r => r.data);

// Parts
export const fetchParts = (params) => api.get("/parts", { params }).then(r => r.data);
// iter136.29 — distinct compressor Product_Types that have at least one PM-kit row registered.
export const fetchPmProductTypes = () => api.get("/compressors/pm-product-types").then(r => r.data);
// iter136.30 — sellable stock (US/CA) per Parts._ComputedKey. Backend returns
// 403 for customers and unauthenticated guests, so callers must guard.
export const fetchSellableStock = () => api.get("/compressors/sellable-stock").then(r => r.data);
export const fetchPart = (id) => api.get(`/parts/${id}`).then(r => r.data);
export const createPart = (data) => api.post("/parts", data).then(r => r.data);
export const updatePart = (id, data) => api.put(`/parts/${id}`, data).then(r => r.data);

// Customers
export const fetchCustomers = (params) => api.get("/customers", { params }).then(r => r.data);
export const fetchCustomer = (id) => api.get(`/customers/${id}`).then(r => r.data);
// iter150AZ.56 — lightweight lookup by string CustomerID returning the
// company name. Used by the Customer Detail header so it never falls
// back to showing the raw hex CustomerID.
export const fetchCustomerSummary = (customerID) =>
  api.get(`/customers/${encodeURIComponent(customerID)}/summary`).then(r => r.data);
export const fetchCustomerPersons = (customerID) => api.get(`/customers/${encodeURIComponent(customerID)}/persons`).then(r => r.data);
export const fetchCustomerQuotes = (customerID, limit = 50) => api.get(`/customers/${encodeURIComponent(customerID)}/quotes`, { params: { limit } }).then(r => r.data);
export const fetchCustomerCompressors = (customerID, limit = 200) => api.get(`/customers/${encodeURIComponent(customerID)}/compressors`, { params: { limit } }).then(r => r.data);
export const fetchCustomerPartsPurchased = (customerID, params = {}) => api.get(`/customers/${encodeURIComponent(customerID)}/parts-purchased`, { params }).then(r => r.data);
export const fetchCustomerJournal = (customerID, limit = 200) => api.get(`/customers/${encodeURIComponent(customerID)}/journal`, { params: { limit } }).then(r => r.data);
export const fetchCustomerPhoneLogs = (customerID, limit = 100) => api.get(`/customers/${encodeURIComponent(customerID)}/phone-logs`, { params: { limit } }).then(r => r.data);
export const fetchCustomerNotes = (customerID, limit = 200) => api.get(`/customers/${encodeURIComponent(customerID)}/notes`, { params: { limit } }).then(r => r.data);
export const fetchCompressorPMSchedule = (serialId) => api.get(`/compressors/${encodeURIComponent(serialId)}/pm-schedule`).then(r => r.data);
export const fetchCompressorServiceForecast = (serialId) => api.get(`/compressors/${encodeURIComponent(serialId)}/service-forecast`).then(r => r.data);
export const fetchCompressorPhoneLogs = (serialId, limit = 100) => api.get(`/compressors/${encodeURIComponent(serialId)}/phone-logs`, { params: { limit } }).then(r => r.data);
export const fetchPersonPhoneLogs = (personId, limit = 100) => api.get(`/persons/${encodeURIComponent(personId)}/phone-logs`, { params: { limit } }).then(r => r.data);
export const fetchQuote = (headingID) => api.get(`/quotes/${encodeURIComponent(headingID)}`).then(r => r.data);
// iter150AI — dealer workflow.
export const fetchQuoteDealerState = (headingID) =>
  api.get(`/quotes/${encodeURIComponent(headingID)}/dealer-state`).then(r => r.data);
export const submitQuoteForApproval = (headingID, payload = {}) =>
  api.post(`/quotes/${encodeURIComponent(headingID)}/submit-for-approval`, payload).then(r => r.data);
export const approveQuote = (headingID, payload) =>
  api.post(`/quotes/${encodeURIComponent(headingID)}/approve`, payload).then(r => r.data);
export const rejectQuote = (headingID, payload) =>
  api.post(`/quotes/${encodeURIComponent(headingID)}/reject`, payload).then(r => r.data);
export const fetchApprovalEmails = () =>
  api.get(`/admin/quote-approval-emails`).then(r => r.data);
export const saveApprovalEmails = (emails) =>
  api.put(`/admin/quote-approval-emails`, { emails }).then(r => r.data);
export const fetchQuoteApprovalHistory = (headingID) =>
  api.get(`/quotes/${encodeURIComponent(headingID)}/approval-history`).then(r => r.data);
export const fetchHelpSection = (section, lang) =>
  api.get(`/help/sections/${encodeURIComponent(section)}`, { params: { lang } }).then(r => r.data);
export const updateQuoteHeading = (headingID, data) => api.put(`/quotes/${encodeURIComponent(headingID)}`, data).then(r => r.data);
export const addQuoteLine = (headingID, data) => api.post(`/quotes/${encodeURIComponent(headingID)}/details`, data).then(r => r.data);
export const updateQuoteLine = (headingID, detailID, data) => api.put(`/quotes/${encodeURIComponent(headingID)}/details/${encodeURIComponent(detailID)}`, data).then(r => r.data);
export const deleteQuoteLine = (headingID, detailID) => api.delete(`/quotes/${encodeURIComponent(headingID)}/details/${encodeURIComponent(detailID)}`).then(r => r.data);
export const searchParts = (q, limit = 20) => api.get(`/parts-search`, { params: { q, limit } }).then(r => r.data);
export const fetchLogbook = (params = {}) => api.get(`/logbook`, { params }).then(r => r.data);
// iter150AI.6 — super-admin API documentation + credentials viewer.
export const fetchApiDocsEndpoints = () => api.get(`/admin/api-docs/endpoints`).then(r => r.data);
export const fetchApiDocsKeys = () => api.get(`/admin/api-docs/keys`).then(r => r.data);
export const fetchLogbookSettings = () => api.get(`/logbook/settings`).then(r => r.data);
// iter150AI.5 — partial update of master flag + per-group modes.
export const updateLogbookSettings = (payload, requester_email) =>
  api.put(`/logbook/settings`, { ...payload, requester_email }).then(r => r.data);
export const fetchLogbookGroups = () => api.get(`/logbook/groups`).then(r => r.data);
export const bulkApplyLogbookSettings = (mode, except_groups, requester_email) =>
  api.put(`/logbook/settings/bulk`, { mode, except_groups, requester_email }).then(r => r.data);
export const fetchLogbookArchiveStatus = () => api.get(`/logbook/archive/status`).then(r => r.data);
export const runLogbookArchive = (requester_email) => api.post(`/logbook/archive/run`, { requester_email }).then(r => r.data);
export const customerCounts = () => api.get("/customers/counts").then(r => r.data);
export const createCustomer = (data) => api.post("/customers", data).then(r => r.data);
export const updateCustomer = (id, data) => api.put(`/customers/${id}`, data).then(r => r.data);

// Admin management
export const fetchAdmins = () => api.get("/admins").then(r => r.data);
export const addAdmin = (email, requester_email) => api.post("/admins", { email, requester_email }).then(r => r.data);
export const removeAdmin = (email, requester_email) => api.delete(`/admins/${encodeURIComponent(email)}`, { params: { requester_email } }).then(r => r.data);
// iter150AZ.88 — Super-admin-managed trusted-domain whitelist
export const addTrustedDomain = (domain, requester_email) => api.post("/admins/trusted-domains", { domain, requester_email }).then(r => r.data);
export const removeTrustedDomain = (domain, requester_email) => api.delete(`/admins/trusted-domains/${encodeURIComponent(domain)}`, { params: { requester_email } }).then(r => r.data);
export const listStaffDomains  = () => api.get("/admins/staff-domains").then(r => r.data);
export const addStaffDomain    = (domain) => api.post("/admins/staff-domains", { domain }).then(r => r.data);
export const removeStaffDomain = (domain) => api.delete(`/admins/staff-domains/${encodeURIComponent(domain)}`).then(r => r.data);

// iter150AZ.91 — Inventory module (gated by ``inventory.access``)
export const fetchInventoryMe         = () => api.get("/inventory/me").then(r => r.data);
export const fetchInventoryParts      = (params = {}) => api.get("/inventory/parts", { params }).then(r => r.data);
export const fetchWarehouses         = () => api.get("/inventory/warehouses").then(r => r.data);
export const createWarehouse         = (data) => api.post("/inventory/warehouses", data).then(r => r.data);
export const updateWarehouse         = (id, data) => api.patch(`/inventory/warehouses/${encodeURIComponent(id)}`, data).then(r => r.data);
export const deleteWarehouse         = (id) => api.delete(`/inventory/warehouses/${encodeURIComponent(id)}`).then(r => r.data);
export const fetchTransfers          = (params = {}) => api.get("/inventory/transfers", { params }).then(r => r.data);
export const createTransfer          = (data) => api.post("/inventory/transfers", data).then(r => r.data);
export const fetchProductsNoOwner    = (params = {}) => api.get("/inventory/products-no-owner", { params }).then(r => r.data);
export const updateProductNoOwner    = (serial_id, data) => api.patch(`/inventory/products-no-owner/${encodeURIComponent(serial_id)}`, data).then(r => r.data);

// iter150AZ.130 — Suppliers (gated by can_view_suppliers / can_edit_suppliers)
export const fetchSuppliers           = (params = {}) => api.get("/suppliers", { params }).then(r => r.data);
export const fetchSupplier            = (id) => api.get(`/suppliers/${encodeURIComponent(id)}`).then(r => r.data);
export const fetchSupplierContacts    = (id) => api.get(`/suppliers/${encodeURIComponent(id)}/contacts`).then(r => r.data);
export const fetchSupplierPurchaseOrders = (id, limit = 100) => api.get(`/suppliers/${encodeURIComponent(id)}/purchase-orders`, { params: { limit } }).then(r => r.data);
export const fetchSupplierNotes       = (id, limit = 50) => api.get(`/suppliers/${encodeURIComponent(id)}/notes`, { params: { limit } }).then(r => r.data);
export const createSupplier           = (data) => api.post("/suppliers", data).then(r => r.data);
export const updateSupplier           = (id, data) => api.put(`/suppliers/${encodeURIComponent(id)}`, data).then(r => r.data);
export const addSupplierContact       = (id, data) => api.post(`/suppliers/${encodeURIComponent(id)}/contacts`, data).then(r => r.data);
export const updateSupplierContact    = (id, cid, data) => api.patch(`/suppliers/${encodeURIComponent(id)}/contacts/${encodeURIComponent(cid)}`, data).then(r => r.data);
export const deleteSupplierContact    = (id, cid) => api.delete(`/suppliers/${encodeURIComponent(id)}/contacts/${encodeURIComponent(cid)}`).then(r => r.data);
export const addSupplierNote          = (id, data) => api.post(`/suppliers/${encodeURIComponent(id)}/notes`, data).then(r => r.data);

// DB credentials (admin)
export const fetchDbConfig = (requester_email) => api.get("/admin/db-config", { params: { requester_email } }).then(r => r.data);
export const testDbConfig = (payload) => api.post("/admin/db-config/test", payload).then(r => r.data);
export const saveDbConfig = (payload) => api.put("/admin/db-config", payload).then(r => r.data);
export const resetDbConfig = (requester_email) => api.delete("/admin/db-config", { params: { requester_email } }).then(r => r.data);

// iter150AZ.109 — DB Ops Gateway config (admin). Secrets are
// encrypted server-side; the API only ever surfaces masks + SHA-256
// fingerprint suffixes. Empty secret fields in saveDbOpsConfig leave
// the previously-stored value untouched; pass "__CLEAR__" to wipe.
export const fetchDbOpsConfig = (requester_email) =>
  api.get("/admin/dbops-config", { params: { requester_email } }).then(r => r.data);
export const testDbOpsConfig = (payload) =>
  api.post("/admin/dbops-config/test", payload).then(r => r.data);
// iter186-v8 — live connectivity probe — calls the gateway's
// GET /v1/db-clients/self/diagnose with the candidate creds.
export const diagnoseDbOpsConfig = (payload) =>
  api.post("/admin/dbops-config/diagnose", payload, { silent: true }).then(r => r.data);
// iter186-v9 — manual self-allow trigger (admin button)
export const manualSelfAllowDbOps = (requester_email) =>
  api.post("/admin/dbops-config/self-allow", { requester_email }, { silent: true }).then(r => r.data);
export const saveDbOpsConfig = (payload) =>
  api.put("/admin/dbops-config", payload).then(r => r.data);
export const resetDbOpsConfig = (requester_email) =>
  api.delete("/admin/dbops-config", { params: { requester_email } }).then(r => r.data);

// ── iter188 (PR 4) — DataAccess boundary admin API ─────────────────
// The DataAccess layer selects between three modes: ``direct`` (existing
// in-process SQL), ``api`` (co-located DB API service), and ``shadow``
// (both, direct is source of truth). These endpoints let a super-admin
// inspect + switch modes without a restart.
export const fetchDataAccessStatus = () =>
  api.get("/admin/data-access/status", { silent: true }).then(r => r.data);
export const setDataAccessMode = ({ mode, reason }) =>
  api.post("/admin/data-access/mode", { mode, reason }, { silent: true }).then(r => r.data);
// iter190 (PR 7) — canary preview comparison. Runs both direct + api
// modes for a single request without changing the global mode.
export const previewDataAccessComparison = ({ method, params }) =>
  api.post("/admin/data-access/preview-comparison",
           { method, params }, { silent: true }).then(r => r.data);

// Product classes (admin)
export const fetchProductClasses = (requester_email) => api.get("/product-classes", { params: { requester_email } }).then(r => r.data);
export const updateProductClass = (id, payload) => api.put(`/product-classes/${id}`, payload).then(r => r.data);

// Compressors by serial
export const fetchCompressors = (params) => api.get("/compressors", { params }).then(r => r.data);
export const compressorsExportUrl = (params = {}) => {
  const qs = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v != null && v !== "")
  ).toString();
  const base = `${process.env.REACT_APP_BACKEND_URL}/api/compressors/export`;
  return qs ? `${base}?${qs}` : base;
};
export const fetchCompressorFiles = (serialId) => api.get(`/compressors/${serialId}/files`).then(r => r.data);
export const resolveCompressorFileUrl = (serialId, path) =>
  api.get(`/compressors/${serialId}/file-url`, { params: { path } }).then(r => r.data);
export const updateCompressorFile = (serialId, pbsid, payload) =>
  api.patch(`/compressors/${serialId}/files/${pbsid}`, payload).then(r => r.data);

// ─── AI Assistant ───
export const fetchAiGreeting = (email, lang = "en") =>
  api.get(`/ai/greeting`, { params: { email, lang } }).then(r => r.data);
export const listAiSessions = (email) => api.get(`/ai/sessions`, { params: { email } }).then(r => r.data);
export const createAiSession = (requester_email, title) => api.post(`/ai/sessions`, { requester_email, title }).then(r => r.data);
export const listAiMessages = (sid, email) => api.get(`/ai/sessions/${sid}/messages`, { params: { email } }).then(r => r.data);
export const sendAiChat = (session_id, message, requester_email, override_params = null) =>
  api.post(`/ai/chat`, {
    session_id,
    message,
    requester_email,
    ...(override_params ? { override_params } : {}),
  }).then(r => r.data);
export const listAiActionHistory = (email, limit = 50) =>
  api.get(`/ai/action-history`, { params: { email, limit } }).then(r => r.data);
export const undoAiAction = (action_id, requester_email) =>
  api.post(`/ai/action-history/${action_id}/undo`, { requester_email }).then(r => r.data);
export const fetchAiProvenance = (entity_type, entity_id, email) =>
  api.get(`/ai/provenance`, { params: { entity_type, entity_id, email } }).then(r => r.data);
export const listAiTraining = (email) => api.get(`/ai/training`, { params: { email } }).then(r => r.data);
export const addAiTraining = (requester_email, fact, source_session_id) => api.post(`/ai/training`, { requester_email, fact, source_session_id }).then(r => r.data);
export const deleteAiTraining = (tid, requester_email) => api.delete(`/ai/training/${tid}`, { data: { requester_email } }).then(r => r.data);

// iter150AZ.12 — Per-reply 👍/👎 rating + admin feedback queue
export const rateAiMessage = (message_id, payload) =>
  api.post(`/ai/messages/${encodeURIComponent(message_id)}/rate`, payload).then(r => r.data);
export const listAiPendingFeedback = (email, limit = 50) =>
  api.get(`/ai/feedback/pending`, { params: { email, limit } }).then(r => r.data);
export const promoteAiFeedback = (fb_id, payload) =>
  api.post(`/ai/feedback/${encodeURIComponent(fb_id)}/promote`, payload).then(r => r.data);
export const dismissAiFeedback = (fb_id, payload) =>
  api.post(`/ai/feedback/${encodeURIComponent(fb_id)}/dismiss`, payload).then(r => r.data);
export const captureAiLead = (session_id, to_email, requester_email, note) =>
  api.post(`/ai/lead`, { session_id, to_email, requester_email, note }).then(r => r.data);
export const listAiLeads = (email, limit = 200) =>
  api.get(`/ai/leads`, { params: { email, limit } }).then(r => r.data);
export const markAiLeadContacted = (lead_id, requester_email) =>
  api.patch(`/ai/leads/${lead_id}/contacted`, { requester_email }).then(r => r.data);
export const previewAiDigest = (email) =>
  api.get(`/ai/digest/preview`, { params: { email } }).then(r => r.data);
export const sendAiDigestNow = (requester_email) =>
  api.post(`/ai/digest/send-now`, { requester_email }).then(r => r.data);
export const fetchAiLeadStats = (email, range, bucket) =>
  api.get(`/ai/leads/stats`, { params: { email, range, bucket } }).then(r => r.data);
export const fetchAiSuggestions = (email, lang = "en") =>
  api.get(`/ai/suggestions`, { params: { email, lang } }).then(r => r.data);
export const fetchAiPromoteRecipients = (payload) =>
  api.post(`/ai/promote/recipients`, payload).then(r => r.data);
export const fetchAiPromoteDraft = (payload) =>
  api.post(`/ai/promote/draft`, payload).then(r => r.data);
export const listAiPromoteDrafts = (email, limit = 50) =>
  api.get(`/ai/promote/drafts`, { params: { email, limit } }).then(r => r.data);
export const fetchAiManual = (role, email = "") =>
  api.get(`/ai/manual/${role}`, { params: { email } }).then(r => r.data);
export const aiManualDownloadUrl = (role, email, format = "pdf") => {
  const base = api.defaults.baseURL || "";
  const params = new URLSearchParams({ email: email || "", format });
  return `${base}/ai/manual/${role}/download?${params.toString()}`;
};
export const fetchWhatsNew = (email) =>
  api.get(`/ai/whats-new`, { params: { email } }).then(r => r.data);
export const markWhatsNewSeen = (requester_email) =>
  api.post(`/ai/whats-new/seen`, { requester_email }).then(r => r.data);

// Part specs (admin-only)
export const fetchPartSpecs = (requester_email, for_class = "") => api.get("/part-specs", { params: { requester_email, for_class } }).then(r => r.data);
export const addPartSpec = (payload) => api.post("/part-specs", payload).then(r => r.data);

// People directory — resolves emails to display names
export const fetchPeople = () => api.get("/people").then(r => r.data);

// Part files
export const fetchPartFiles = (partId, audience = "all") => api.get(`/parts/${partId}/files`, { params: { audience } }).then(r => r.data);
export const resolveDriveFile = (name, kind = "file") => api.get("/drive/resolve", { params: { name, kind } }).then(r => r.data);

// My-uploads inbox
export const fetchMyUploads = () => api.get("/my-uploads").then(r => r.data);
export const fetchMyUploadsReps = () => api.get("/my-uploads/reps").then(r => r.data);
export const shareMyUpload = (fileId, payload = {}) =>
  api.post(`/my-uploads/${encodeURIComponent(fileId)}/share`, payload).then(r => r.data);

// Public Files — MySQL `Email Data` catalogue (ACC/USAir documents)
export const fetchPublicFiles = (params = {}) =>
  api.get("/public-files", { params }).then(r => r.data);
export const fetchPublicFileClasses = () =>
  api.get("/public-files/classes").then(r => r.data);
export const resolvePublicFiles = (query, limit = 10) =>
  api.post("/public-files/resolve", { query, limit }).then(r => r.data);
export const sendPublicFiles = (payload) =>
  api.post("/public-files/send", payload).then(r => r.data);
export const adminUpdatePublicFile = (id, patch) =>
  api.put(`/public-files/${id}`, patch).then(r => r.data);
export const adminDeletePublicFile = (id) =>
  api.delete(`/public-files/${id}`).then(r => r.data);
export const adminCreatePublicFile = (file, meta, onProgress) => {
  const fd = new FormData();
  fd.append("upload", file);
  fd.append("file_type", meta.file_type);
  if (meta.file_class) fd.append("file_class", meta.file_class);
  if (meta.explanation) fd.append("explanation", meta.explanation);
  fd.append("available_for_customer", meta.available_for_customer ? "true" : "false");
  fd.append("allowed_with_quote", meta.allowed_with_quote ? "true" : "false");
  fd.append("display_name", meta.display_name || file.name);
  return api.post("/public-files", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: onProgress
      ? (e) => onProgress(Math.round((e.loaded * 100) / (e.total || 1)))
      : undefined,
  }).then(r => r.data);
};
export const publicFileDownloadUrl = (id) =>
  `${process.env.REACT_APP_BACKEND_URL}/api/public-files/${encodeURIComponent(id)}/download`;
export const publicFilePictureUrl = (id) =>
  `${process.env.REACT_APP_BACKEND_URL}/api/public-files/${encodeURIComponent(id)}/picture`;

// Public-file send templates (saved selections)
export const fetchPublicFileTemplates = () =>
  api.get("/public-file-templates").then(r => r.data);
export const createPublicFileTemplate = (payload) =>
  api.post("/public-file-templates", payload).then(r => r.data);
export const updatePublicFileTemplate = (id, payload) =>
  api.put(`/public-file-templates/${id}`, payload).then(r => r.data);
export const deletePublicFileTemplate = (id) =>
  api.delete(`/public-file-templates/${id}`).then(r => r.data);

// Upload a file (or photo / video on mobile) to a Part. `opts` may include
// audience, explanation, file_type, and `onProgress(pct)` for the UI.
export const uploadPartFile = (partId, file, opts = {}) => {
  const fd = new FormData();
  fd.append("upload", file);
  if (opts.audience) fd.append("audience", opts.audience);
  if (opts.explanation) fd.append("explanation", opts.explanation);
  if (opts.file_type) fd.append("file_type", opts.file_type);
  return api.post(`/parts/${partId}/files`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (evt) => {
      if (opts.onProgress && evt.total) {
        opts.onProgress(Math.round((evt.loaded / evt.total) * 100));
      }
    },
  }).then((r) => r.data);
};

// Admin: files base URL
export const getFilesBaseUrl = (requester_email) => api.get("/admin/files-base-url", { params: { requester_email } }).then(r => r.data);
export const setFilesBaseUrl = (url, requester_email) => api.put("/admin/files-base-url", { url, requester_email }).then(r => r.data);

// Admin: Google Drive config
export const getDriveConfig = (requester_email) => api.get("/admin/drive-config", { params: { requester_email } }).then(r => r.data);
export const testDriveConfig = (payload) => api.post("/admin/drive-config/test", payload).then(r => r.data);
export const saveDriveConfig = (payload) => api.put("/admin/drive-config", payload).then(r => r.data);
// iter178 — server-side folder browse (Drive API key only) + Picker creds
export const browseDriveFolder = (requester_email, folder_id) =>
  api.get("/admin/drive-config/browse", { params: { requester_email, folder_id } }).then(r => r.data);
export const getDrivePickerCredentials = (requester_email) =>
  api.get("/admin/drive-config/picker-credentials", { params: { requester_email } }).then(r => r.data);
// iter179 — re-scan canonical subfolders under the saved root
export const rescanDriveSubfolders = (requester_email) =>
  api.post("/admin/drive-config/rescan", null, { params: { requester_email } }).then(r => r.data);
// iter180 — audit every DB file path against the new Drive resolver
export const auditDrivePaths = (requester_email, per_source_limit = 200) =>
  api.post("/admin/drive-config/audit", null, {
    params: { requester_email, per_source_limit },
    timeout: 120000,
  }).then(r => r.data);
// iter181 — best-effort auto-detect the root folder by searching for
// the canonical subfolder names via the saved API key
export const autodetectDriveRoot = (requester_email) =>
  api.post("/admin/drive-config/autodetect-root", null, {
    params: { requester_email },
    timeout: 60000,
  }).then(r => r.data);
// iter182 — OAuth-powered "smart locator". Once Drive is connected,
// learn-all walks every DB file column, finds a sample, and registers
// the parent folder so every later lookup is a single Drive call.
export const getLocatorStatus = (requester_email) =>
  api.get("/admin/drive-config/locator/status", { params: { requester_email } }).then(r => r.data);
export const learnAllDriveFields = (requester_email) =>
  api.post("/admin/drive-config/locator/learn-all", null, {
    params: { requester_email },
    timeout: 180000,
  }).then(r => r.data);
export const clearLocatorRegistry = (requester_email, table = null, column = null) =>
  api.delete("/admin/drive-config/locator/registry", {
    params: { requester_email, ...(table ? { table } : {}), ...(column ? { column } : {}) },
  }).then(r => r.data);
// iter182 — Drive OAuth: trigger the connect flow + read status
export const getDriveOAuthStatus = (requester_email) =>
  api.get("/admin/drive-oauth/status", { params: { requester_email } }).then(r => r.data);
// iter184 — Ping ``drive.about.get`` and surface the connected
// Google account's email + storage so the operator can verify the
// right account ended up authorized at a glance.
export const testDriveOAuthConnection = (requester_email) =>
  api.get("/admin/drive-oauth/test", { params: { requester_email }, timeout: 15000 }).then(r => r.data);
// iter183 — optional ``login_hint`` lets the caller pin which Google
// account the consent screen should pre-select (e.g. the one that
// owns the Drive folders we need to read).
export const startDriveOAuth = (requester_email, return_to = "/admin/settings", login_hint = "") =>
  api.post("/admin/drive-oauth/start", {
    requester_email, return_to, login_hint,
  }).then(r => r.data);

// Export
export const exportPriceList = (data) => api.post("/export", data, { responseType: "blob" }).then(r => r);

// Cart checkout
export const cartCheckout = (payload) => api.post("/cart/checkout", payload).then(r => r.data);
export const prepareCartShippingDraft = (payload) =>
  api.post("/cart/checkout/shipping-draft", payload).then(r => r.data);
export const previewCustomerTier = (customerId) => api.get(`/cart/preview-tier/${encodeURIComponent(customerId)}`).then(r => r.data);

// Generate a public share link for a quote (admin-only)
export const createQuoteShareLink = (quoteId) =>
  api.post(`/quotes/${encodeURIComponent(quoteId)}/share-link`).then(r => r.data);

// Quote change history (for the admin Quote page)
export const fetchQuoteHistory = (quoteId, params = {}) =>
  api.get(`/quotes/${encodeURIComponent(quoteId)}/history`, { params }).then(r => r.data);

// Related quotes for the same customer (admin/owner/magic-token)
export const fetchQuoteRelated = (quoteId, params = {}) =>
  api.get(`/quotes/${encodeURIComponent(quoteId)}/related`, { params }).then(r => r.data);

// Payment summary (Journal Entry rows linked to this quote — for header chip)
export const fetchQuotePayments = (quoteId, params = {}) =>
  api.get(`/quotes/${encodeURIComponent(quoteId)}/payments`, { params }).then(r => r.data);

// v1 snapshot + current state + computed diffs
export const fetchQuoteVersions = (quoteId, params = {}) =>
  api.get(`/quotes/${encodeURIComponent(quoteId)}/versions`, { params }).then(r => r.data);

// Absolute URL to the Proforma Invoice PDF for this quote
export const quotePdfUrl = (quoteId, magicToken = "") => {
  const base = `${BACKEND_URL}/api/quotes/${encodeURIComponent(quoteId)}/pdf`;
  return magicToken ? `${base}?t=${encodeURIComponent(magicToken)}` : base;
};

// Quote comments / replies thread
export const fetchQuoteComments = (quoteId, params = {}) =>
  api.get(`/quotes/${encodeURIComponent(quoteId)}/comments`, { params }).then(r => r.data);

export const postQuoteComment = (quoteId, payload) =>
  api.post(`/quotes/${encodeURIComponent(quoteId)}/comments`, payload).then(r => r.data);

// Upload an attachment to a quote (pre-comment). Returns the attachment metadata.
export const uploadQuoteAttachment = (quoteId, file, magicToken = "") => {
  const form = new FormData();
  form.append("file", file);
  if (magicToken) form.append("magic_token", magicToken);
  return api.post(`/quotes/${encodeURIComponent(quoteId)}/attachments`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  }).then(r => r.data);
};

// Promote a Preliminary draft to a final Quote (admin-only)
export const promoteQuote = (quoteId) =>
  api.post(`/quotes/${encodeURIComponent(quoteId)}/promote`).then(r => r.data);

export const sendQuoteToCustomer = (quoteId, payload) =>
  api.post(`/quotes/${encodeURIComponent(quoteId)}/send`, payload).then(r => r.data);

// iter150AZ.10 — Email me one preview PDF per recipient language so I can
// see how every contact will read the quote before sending it for real.
export const sendQuoteSampleToMe = (quoteId) =>
  api.post(`/quotes/${encodeURIComponent(quoteId)}/send-sample-to-me`).then(r => r.data);

export const fetchQuoteEmailSends = (quoteId, params = {}) =>
  api.get(`/quotes/${encodeURIComponent(quoteId)}/email-sends`, { params }).then(r => r.data);

// Compressor lines on a quote that have a Preventive Maintenance schedule
export const fetchQuotePmCandidates = (quoteId) =>
  api.get(`/quotes/${encodeURIComponent(quoteId)}/pm-candidates`).then(r => r.data);

// Typing indicator (heartbeat + poll) for a quote's comments thread
export const sendTypingHeartbeat = (quoteId, payload) =>
  api.post(`/quotes/${encodeURIComponent(quoteId)}/comments/typing`, payload).then(r => r.data);

export const fetchTypingUsers = (quoteId, params = {}) =>
  api.get(`/quotes/${encodeURIComponent(quoteId)}/comments/typing`, { params }).then(r => r.data);

export default api;

// ─── Access Control (RBAC) ────────────────────────────────────────
export const fetchAccessMe = () => api.get("/access/me").then(r => r.data);
export const fetchAccessPermissions = () => api.get("/access/permissions").then(r => r.data);
export const fetchAccessRoles = () => api.get("/access/roles").then(r => r.data);
export const fetchAccessUsers = (params = {}) => api.get("/access/users", { params }).then(r => r.data);
export const upsertAccessUser = (payload) => api.put("/access/users", payload).then(r => r.data);
export const deleteAccessUser = (email) => api.delete(`/access/users/${encodeURIComponent(email)}`).then(r => r.data);
export const searchAccessPeople = (params) => api.get("/access/people/search", { params }).then(r => r.data);
export const upsertAccessRole = (payload) => api.post("/access/roles", payload).then(r => r.data);
export const deleteAccessRole = (role) => api.delete(`/access/roles/${encodeURIComponent(role)}`).then(r => r.data);

// Access-control advisor (deterministic overlap + LLM agent)
export const advisorCheckPermission = (payload) => api.post("/access/agent/check-permission", payload).then(r => r.data);
export const advisorCheckRole = (payload) => api.post("/access/agent/check-role", payload).then(r => r.data);
export const advisorAnalyseAssignment = (payload) => api.post("/access/agent/analyse-assignment", payload).then(r => r.data);
export const advisorChat = (prompt) => api.post("/access/agent/chat", { prompt }).then(r => r.data);
export const advisorApply = (payload) => api.post("/access/agent/apply", payload).then(r => r.data);
export const advisorUndo = (applyId) => api.post(`/access/agent/undo/${encodeURIComponent(applyId)}`).then(r => r.data);
export const advisorHistory = (params = {}) => api.get("/access/agent/history", { params }).then(r => r.data);
export const fetchRecentQuoteChanges = (params = {}) => api.get("/quotes-feed/recent", { params }).then(r => r.data);

// Email routing (admin)
export const fetchEmailRouting = (requester_email) => api.get("/admin/email/routing", { params: { requester_email } }).then(r => r.data);
export const saveEmailRouting = (payload) => api.put("/admin/email/routing", payload).then(r => r.data);
export const sendTestEmail = (payload) => api.post("/admin/email/test", payload).then(r => r.data);


// ─── PM Schedule ───
export const fetchPmSchedule = (product_type, shifts = 1, years = 10, currency = "USD", include_prices = true) =>
  api.get("/compressors/pm-schedule", {
    params: { product_type, shifts, years, currency, include_prices },
  }).then(r => r.data);

export const fetchStarterKits = (product_type, currency = "USD", shifts = 1, years = 3) =>
  api.get("/compressors/starter-kits", {
    params: { product_type, currency, shifts, years },
  }).then(r => r.data);

export const pmSchedulePdfUrl = ({ product_type, shifts = 1, years = 10, brand = "US", customer_name = "", quote_id = "", include_prices = true }) => {
  const qs = new URLSearchParams({
    product_type, shifts: String(shifts), years: String(years), brand,
    include_prices: String(!!include_prices),
    ...(customer_name ? { customer_name } : {}),
    ...(quote_id ? { quote_id } : {}),
  }).toString();
  return `${BACKEND_URL}/api/compressors/pm-schedule/pdf?${qs}`;
};

// ─── Twilio: Video / SMS / Whisper transcription ───────────────────
export const twilioHealth = () => api.get("/twilio/health").then(r => r.data);
export const mintVideoToken = ({ room, identity, customer_id }) =>
  api.post("/twilio/video/token", { room, identity, customer_id }).then(r => r.data);
export const logVideoEnd = ({ room, note, duration_seconds }) =>
  api.post("/twilio/video/log", { room, note, duration_seconds }).then(r => r.data);
export const fetchCustomerCalls = (customer_id, limit = 50) =>
  api.get(`/twilio/calls/${encodeURIComponent(customer_id)}`, { params: { limit } }).then(r => r.data);
export const sendSms = ({ customer_id, person_id, to, body }) =>
  api.post("/twilio/sms/send", { customer_id, person_id, to, body }).then(r => r.data);
export const fetchSmsThread = (customer_id, limit = 200, phone = null) =>
  api.get(`/twilio/sms/threads/${encodeURIComponent(customer_id)}`,
          { params: { limit, ...(phone ? { phone } : {}) } }).then(r => r.data);
export const fetchSmsThreadByPhone = (phone, limit = 200) => {
  // urlsafe-base64 of the phone (server tolerates missing padding)
  const b64 = btoa(unescape(encodeURIComponent(String(phone || ""))))
    .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  return api.get(`/twilio/sms/threads-by-phone/${b64}`, { params: { limit } }).then(r => r.data);
};
export const searchPersonsForLink = (q, limit = 20) =>
  api.get("/twilio/sms/persons/search", { params: { q, limit } }).then(r => r.data);
export const linkOrphanToPerson = (payload) =>
  api.post("/twilio/sms/orphan/link", payload).then(r => r.data);

export const markConversationUnread = (customer_id, person_id, phone_number) => {
  if (!customer_id) {
    return api.post(`/twilio/sms/orphan-thread/mark-unread`, null,
                    { params: { phone_number } }).then(r => r.data);
  }
  return api.post(`/twilio/sms/conversations/${encodeURIComponent(customer_id)}/mark-unread`, null,
                  { params: { ...(person_id ? { person_id } : {}),
                              ...(phone_number ? { phone_number } : {}) } }).then(r => r.data);
};
export const blockNumber = (phone, reason) =>
  api.post(`/twilio/sms/blocked-numbers`, { phone, reason }).then(r => r.data);
export const unblockNumber = (phone) =>
  api.delete(`/twilio/sms/blocked-numbers`, { params: { phone } }).then(r => r.data);
export const fetchBlockedNumbers = (limit = 200) =>
  api.get(`/twilio/sms/blocked-numbers`, { params: { limit } }).then(r => r.data);
export const fetchAllConversations = (limit = 100, archived = false) =>
  api.get(`/twilio/sms/conversations`, { params: { limit, archived } }).then(r => r.data);
export const archiveConversation = (customer_id, person_id, phone_number) => {
  if (!customer_id) {
    return api.post(`/twilio/sms/orphan-thread/archive`, null,
                    { params: { phone_number } }).then(r => r.data);
  }
  return api.post(`/twilio/sms/conversations/${encodeURIComponent(customer_id)}/archive`, null,
                  { params: { ...(person_id ? { person_id } : {}),
                              ...(phone_number ? { phone_number } : {}) } }).then(r => r.data);
};
export const snoozeConversation = (customer_id, person_id, phone_number) => {
  if (!customer_id) {
    return api.post(`/twilio/sms/orphan-thread/archive`, null,
                    { params: { phone_number, snooze: true } }).then(r => r.data);
  }
  return api.post(`/twilio/sms/conversations/${encodeURIComponent(customer_id)}/snooze`, null,
                  { params: { ...(person_id ? { person_id } : {}),
                              ...(phone_number ? { phone_number } : {}) } }).then(r => r.data);
};
export const unarchiveConversation = (customer_id, person_id, phone_number) => {
  if (!customer_id) {
    return api.post(`/twilio/sms/orphan-thread/unarchive`, null,
                    { params: { phone_number } }).then(r => r.data);
  }
  return api.post(`/twilio/sms/conversations/${encodeURIComponent(customer_id)}/unarchive`, null,
                  { params: { ...(person_id ? { person_id } : {}),
                              ...(phone_number ? { phone_number } : {}) } }).then(r => r.data);
};
export const autoArchiveStaleThreads = (stale_days = 90) =>
  api.post(`/twilio/sms/conversations/auto-archive-stale`, null,
           { params: { stale_days } }).then(r => r.data);
export const markConversationSeen = (customer_id, person_id) =>
  api.post(`/twilio/sms/conversations/${encodeURIComponent(customer_id)}/mark-seen`, null,
           { params: person_id ? { person_id } : {} }).then(r => r.data);
export const deleteConversation = (customer_id, person_id, phone_number) => {
  if (!customer_id) {
    return api.delete(`/twilio/sms/orphan-thread`,
                      { params: { phone_number } }).then(r => r.data);
  }
  return api.delete(`/twilio/sms/conversations/${encodeURIComponent(customer_id)}`,
                    { params: { ...(person_id ? { person_id } : {}),
                                ...(phone_number ? { phone_number } : {}) } }).then(r => r.data);
};
export const refreshSmsStatus = (customer_id) =>
  api.post(`/twilio/sms/refresh-status/${encodeURIComponent(customer_id)}`).then(r => r.data);
export const transcribeAudio = ({ blob, customer_id, call_id, save_as_note = true }) => {
  const fd = new FormData();
  fd.append("audio", blob, blob.name || "call.webm");
  fd.append("customer_id", customer_id);
  if (call_id) fd.append("call_id", call_id);
  fd.append("save_as_note", String(save_as_note));
  return api.post("/twilio/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data);
};

// ─── Service-Tech on-site capture ──────────────────────────────────
export const fetchServiceTechFleet = (q = "", limit = 200) =>
  api.get("/service-tech/fleet", { params: { q, limit } }).then(r => r.data);
export const fetchServiceTechOpenQuote = (serial_id) =>
  api.get(`/service-tech/fleet/${encodeURIComponent(serial_id)}/open-quote`).then(r => r.data);
export const createServiceVisit = (payload) =>
  api.post("/service-tech/visits", payload).then(r => r.data);
export const listServiceVisits = (limit = 50) =>
  api.get("/service-tech/visits", { params: { limit } }).then(r => r.data);
export const getServiceVisit = (visit_id) =>
  api.get(`/service-tech/visits/${encodeURIComponent(visit_id)}`).then(r => r.data);
export const finalizeServiceVisit = (visit_id, payload = {}) =>
  api.post(`/service-tech/visits/${encodeURIComponent(visit_id)}/finalize`, payload).then(r => r.data);
export const fetchServiceTechDashboard = (params = {}) =>
  api.get("/service-tech/dashboard", { params }).then(r => r.data);
export const previewServiceTechDigest = (params = {}) =>
  api.get("/service-tech/digest/preview", { params }).then(r => r.data);
export const sendServiceTechDigest = (payload = {}) =>
  api.post("/service-tech/digest/send", payload).then(r => r.data);
export const previewServiceVisitSn = (visit_id) =>
  api.post(`/service-tech/visits/${encodeURIComponent(visit_id)}/preview-sn`).then(r => r.data);
export const fetchServiceTechLeaderboard = (days = 30) =>
  api.get("/service-tech/leaderboard", { params: { days } }).then(r => r.data);

// ─── Employee Positions admin ──────────────────────────────────────
export const fetchPositions = (q = "") => api.get("/positions", { params: { q } }).then(r => r.data);
export const createPosition = (payload) => api.post("/positions", payload).then(r => r.data);
export const updatePosition = (ep_id, payload) =>
  api.put(`/positions/${encodeURIComponent(ep_id)}`, payload).then(r => r.data);
export const deletePosition = (ep_id) => api.delete(`/positions/${encodeURIComponent(ep_id)}`).then(r => r.data);
export const uploadServiceVisitMedia = (visit_id, kind, file, opts = {}) => {
  const fd = new FormData();
  fd.append("upload", file, file.name || `${kind}.bin`);
  fd.append("kind", kind);
  return api.post(`/service-tech/visits/${encodeURIComponent(visit_id)}/media`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (evt) => {
      if (opts.onProgress && evt.total) {
        opts.onProgress(Math.round((evt.loaded / evt.total) * 100));
      }
    },
  }).then(r => r.data);
};
export const serviceVisitMediaUrl = (media_id) =>
  `${BACKEND_URL}/api/service-tech/media/${encodeURIComponent(media_id)}`;


// Call routing + staff-position grid (iter54)
export const fetchCallRoutingRules = () => api.get("/admin/call-routing/rules").then(r => r.data);
export const updateCallRoutingRules = (data) => api.put("/admin/call-routing/rules", data).then(r => r.data);
export const fetchStaffPositions = () => api.get("/admin/staff-positions").then(r => r.data);
export const updateStaffPosition = (data) => api.patch("/admin/staff-positions", data).then(r => r.data);
export const previewVoiceWebhooks = () => api.get("/admin/twilio-credentials/voice-config/preview").then(r => r.data);
export const syncVoiceWebhooks = (phone_sids = null) => api.post("/admin/twilio-credentials/voice-config/sync", { phone_sids }).then(r => r.data);
export const fetchWarrantyBriefings = (params = {}) => api.get("/admin/warranty-briefings", { params }).then(r => r.data);
export const fetchMyRecentBriefings = (limit = 20) => api.get("/me/recent-briefings", { params: { limit } }).then(r => r.data);
export const fetchErrorLog = (requester_email, params = {}) => api.get("/admin/error-log", { params: { requester_email, ...params } }).then(r => r.data);
export const fetchErrorRow = (incident_id, requester_email) => api.get(`/admin/error-log/${encodeURIComponent(incident_id)}`, { params: { requester_email } }).then(r => r.data);

// iter136.28 — PWA branding (super-admin-editable title + icons)
export const fetchPwaBranding = () => api.get("/pwa/branding").then(r => r.data);
export const updatePwaBranding = (patch) => api.put("/pwa/branding", patch).then(r => r.data);
export const resetPwaBranding = () => api.delete("/pwa/branding").then(r => r.data);
export const uploadPwaIcon = (file) => {
  const fd = new FormData();
  fd.append("upload", file);
  return api.post("/pwa/icon", fd, { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data);
};


// ─── HR Admin (iter147) ──────────────────────────────────────────
export const fetchHrStaff = () => api.get("/hr/staff").then(r => r.data);
export const fetchHrInsights = () => api.get("/hr/insights").then(r => r.data);
export const fetchHrLibrary = (params = {}) => api.get("/hr/library", { params }).then(r => r.data);
export const createHrLibraryItem = (payload) => api.post("/hr/library", payload).then(r => r.data);
export const updateHrLibraryItem = (id, payload) => api.put(`/hr/library/${id}`, payload).then(r => r.data);
export const deleteHrLibraryItem = (id) => api.delete(`/hr/library/${id}`).then(r => r.data);
export const publishHrLibraryItem = (id) => api.post(`/hr/library/${id}/publish`).then(r => r.data);
export const generateHrMaterial = (payload) => api.post("/hr/ai/generate", payload).then(r => r.data);
export const sendHrChatTurn = (payload) => api.post("/hr/ai/chat", payload).then(r => r.data);
export const fetchHrChatSession = (sessionId) => api.get(`/hr/ai/chat/${sessionId}`).then(r => r.data);
export const translateHrLibraryItem = (id) => api.post(`/hr/library/${id}/translate`).then(r => r.data);
export const recordHrLibraryView = (id) => api.post(`/hr/library/${id}/view`).then(r => r.data);

// Checklists (iter148)
export const fetchHrTemplates = () => api.get("/hr/checklists/templates").then(r => r.data);
export const createHrTemplate = (p) => api.post("/hr/checklists/templates", p).then(r => r.data);
export const updateHrTemplate = (id, p) => api.put(`/hr/checklists/templates/${id}`, p).then(r => r.data);
export const deleteHrTemplate = (id) => api.delete(`/hr/checklists/templates/${id}`).then(r => r.data);
export const fetchHrAssignments = () => api.get("/hr/checklists/assignments").then(r => r.data);
export const createHrAssignments = (p) => api.post("/hr/checklists/assignments", p).then(r => r.data);
export const deleteHrAssignment = (id) => api.delete(`/hr/checklists/assignments/${id}`).then(r => r.data);
export const fetchMyChecklist = () => api.get("/hr/checklists/me").then(r => r.data);
export const ackMyChecklistItem = (item_id) => api.post("/hr/checklists/me/ack", { item_id }).then(r => r.data);
export const hrMarkChecklistItem = (p) => api.post("/hr/checklists/hr-mark", p).then(r => r.data);

// Admin manuals JSON editor (iter149c)
export const fetchManualFiles = () => api.get("/admin/manuals").then(r => r.data);
export const fetchManualFile = (name) => api.get(`/admin/manuals/${name}`).then(r => r.data);
export const saveManualFile = (name, content) => api.put(`/admin/manuals/${name}`, { content }).then(r => r.data);


// ─── Direct-vendor shipping (iter150AZ.132) ────────────────────────
// Super-admins manage carrier/finance credentials and adjust the
// shipping state machine here. All endpoints require the
// SHIPPING_USE_DIRECT_VENDORS feature flag (or ?direct=1) to be on.
const shippingModeParams = (direct) =>
  direct == null ? {} : { direct: direct ? 1 : 0 };
export const fetchShippingHealth = ({ direct = null } = {}) =>
  api.get("/shipping/health", { params: shippingModeParams(direct) }).then(r => r.data);
export const fetchShippingCredentials = () =>
  api.get("/shipping/credentials").then(r => r.data);
export const saveShippingCredentials = (vendor, fields, mode) =>
  api.put(`/shipping/credentials/${vendor}`, { fields, mode }).then(r => r.data);
export const deleteShippingCredentials = (vendor) =>
  api.delete(`/shipping/credentials/${vendor}`).then(r => r.data);
export const deleteShippingCredentialField = (vendor, field) =>
  api.delete(`/shipping/credentials/${encodeURIComponent(vendor)}/fields/${encodeURIComponent(field)}`).then(r => r.data);
export const testShippingCredentials = (vendor) =>
  api.post(`/shipping/credentials/${vendor}/test`).then(r => r.data);

// Direct-vendor quote shipping flow (replaces /calculate).
export const collectShippingRates = (quoteId, { direct = null } = {}) =>
  api.post(`/quotes/${encodeURIComponent(quoteId)}/shipping/rates`,
              {}, { params: shippingModeParams(direct) }).then(r => r.data);
export const selectShippingRate = (quoteId, rateId, reason,
                                   { direct = null } = {}) =>
  api.post(`/quotes/${encodeURIComponent(quoteId)}/shipping/select-rate`,
              { rate_id: rateId, reason },
              { params: shippingModeParams(direct) }).then(r => r.data);
export const bookShipments = (quoteId, { direct = null } = {}) =>
  api.post(`/quotes/${encodeURIComponent(quoteId)}/shipping/book`,
              { authorize_charges: true },
              { params: shippingModeParams(direct) }).then(r => r.data);
export const fetchQuoteShipping = (quoteId, { direct = null } = {}) =>
  api.get(`/quotes/${encodeURIComponent(quoteId)}/shipping`,
              { params: shippingModeParams(direct) }).then(r => r.data);

// Per-shipment management (super-admin reads / admin writes).
export const fetchOrderShipping = (orderId) =>
  api.get(`/orders/${encodeURIComponent(orderId)}/shipping`).then(r => r.data);
export const trackShipment = (shipmentId) =>
  api.post(`/shipments/${encodeURIComponent(shipmentId)}/track`).then(r => r.data);
export const cancelShipment = (shipmentId, body) =>
  api.post(`/shipments/${encodeURIComponent(shipmentId)}/cancel`,
              body).then(r => r.data);
export const reconcileShipmentBooking = (shipmentId, body) =>
  api.post(`/shipments/${encodeURIComponent(shipmentId)}/booking/reconcile`,
              body).then(r => r.data);
export const syncShipmentDocuments = (shipmentId) =>
  api.post(`/shipments/${encodeURIComponent(shipmentId)}/documents/sync`).then(r => r.data);
export const fetchShipmentDocuments = (shipmentId) =>
  api.get(`/shipments/${encodeURIComponent(shipmentId)}/documents`).then(r => r.data);
export const fetchShipmentAudit = (shipmentId) =>
  api.get(`/shipments/${encodeURIComponent(shipmentId)}/audit`).then(r => r.data);
export const financeSyncShipment = (shipmentId) =>
  api.post(`/shipments/${encodeURIComponent(shipmentId)}/finance-sync`).then(r => r.data);
export const createCustomsCase = (shipmentId, body = {}) =>
  api.post(`/shipments/${encodeURIComponent(shipmentId)}/customs`,
              body).then(r => r.data);
export const updateCustomsStatus = (shipmentId, body) =>
  api.post(`/shipments/${encodeURIComponent(shipmentId)}/customs/status`,
              body).then(r => r.data);
export const uploadCustomsDocument = (shipmentId, documentType, file) => {
  const form = new FormData();
  form.append("document_type", documentType);
  form.append("upload", file);
  return api.post(
    `/shipments/${encodeURIComponent(shipmentId)}/customs/documents`,
    form,
  ).then((response) => response.data);
};


// ─── Vendor Shipments (Supplier Invoices) — iter171 ────────────────
export const fetchVendorShipments = (params = {}) =>
  api.get("/vendor-shipments", { params }).then(r => r.data);
export const fetchVendorShipment = (id) =>
  api.get(`/vendor-shipments/${encodeURIComponent(id)}`).then(r => r.data);
export const fetchSupplierVendorShipments = (supplierId, limit = 200) =>
  api.get(`/suppliers/${encodeURIComponent(supplierId)}/vendor-shipments`,
              { params: { limit } }).then(r => r.data);
export const fetchPurchaseOrderVendorShipments = (poId) =>
  api.get(`/purchase-orders/${encodeURIComponent(poId)}/vendor-shipments`)
        .then(r => r.data);
export const fetchPurchaseOrder = (poId) =>
  api.get(`/purchase-orders/${encodeURIComponent(poId)}`).then(r => r.data);
export const fetchVendorShipmentsSchema = () =>
  api.get("/vendor-shipments/_schema").then(r => r.data);



